import { useState, useEffect } from 'react'
import { KeyRound } from 'lucide-react'
import { api } from '@/lib/api'
import s from './sso-buttons.module.css'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export interface SsoProviders {
  google: boolean
  microsoft: boolean
  apple: boolean
}

const NO_PROVIDERS: SsoProviders = { google: false, microsoft: false, apple: false }

/**
 * Which SSO providers are live (`/auth/sso/providers`) — null while loading.
 * Pass `skip` when the caller already has the answer (e.g. it was fetched by a
 * parent that also renders SsoButtons) so the endpoint isn't hit twice.
 */
export function useSsoProviders(skip = false): SsoProviders | null {
  const [providers, setProviders] = useState<SsoProviders | null>(null)

  useEffect(() => {
    if (skip) return
    let active = true
    api<SsoProviders>('/auth/sso/providers')
      .then((res) => { if (active) setProviders(res) })
      .catch(() => {
        // SSO optional — leave every provider off
        if (active) setProviders(NO_PROVIDERS)
      })
    return () => { active = false }
  }, [skip])

  return providers
}

// Official multi-color Google "G". Exported for the outreach-signup Google
// connect step's CTA; everything else here stays module-scoped.
export function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

// Microsoft four-square logo.
function MicrosoftIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  )
}

// Apple mark, currentColor so it inherits the button's near-black text.
function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )
}

function startGoogle() {
  window.location.href = `${API_BASE}/auth/sso/google`
}
function startMicrosoft() {
  window.location.href = `${API_BASE}/auth/sso/microsoft`
}
function startApple() {
  window.location.href = `${API_BASE}/auth/sso/apple`
}

/**
 * Google / Microsoft / Apple SSO buttons. Self-fetches which providers are
 * configured (via useSsoProviders) unless the parent passes `providers`, and
 * only renders a button when that provider is live, so nothing shows for an
 * unconfigured provider. Renders null if none are on. Logo pinned left, label
 * optically centered. `dividerLabel` adds an "or …" rule.
 *
 * `variant="dark"` remains available for explicitly dark surfaces. The auth
 * shell uses the default light variant to match the Corpus signup language.
 */
export function SsoButtons({
  dividerLabel,
  onPasskey,
  providers,
  variant = 'light',
}: {
  dividerLabel?: string
  onPasskey?: () => void
  providers?: SsoProviders | null
  variant?: 'light' | 'dark'
}) {
  const fetched = useSsoProviders(providers !== undefined)
  const sso = providers ?? fetched ?? NO_PROVIDERS

  if (!sso.google && !sso.microsoft && !sso.apple && !onPasskey) return null

  return (
    <div className={variant === 'dark' ? [s.stack, s.stackDark].join(' ') : s.stack}>
      {onPasskey && (
        <button type="button" className={s.btn} onClick={onPasskey}>
          <span className={s.icon}><KeyRound size={18} /></span>
          Continue with a passkey
        </button>
      )}
      {sso.google && (
        <button type="button" className={s.btn} onClick={startGoogle}>
          <span className={s.icon}><GoogleIcon /></span>
          Continue with Google
        </button>
      )}
      {sso.microsoft && (
        <button type="button" className={s.btn} onClick={startMicrosoft}>
          <span className={s.icon}><MicrosoftIcon /></span>
          Continue with Microsoft
        </button>
      )}
      {sso.apple && (
        <button type="button" className={s.btn} onClick={startApple}>
          <span className={s.icon}><AppleIcon /></span>
          Continue with Apple
        </button>
      )}
      {dividerLabel && (
        <div className={s.divider}>
          <span className={s.dividerRule} />
          {dividerLabel}
          <span className={s.dividerRule} />
        </div>
      )}
    </div>
  )
}
